/**
 * Normalizes a full URL string by:
 * - Lowercasing hostname and pathname
 * - Deduplicating slashes in pathname
 * - Ensuring pathname ends with a trailing slash (except root)
 */
export function normalizeFullUrlToConsistentFormat(url: string): string {
    try {
        const u = new URL(url);
        u.hostname = u.hostname.toLowerCase();

        // Step-by-step for clarity and debuggability
        u.pathname = u.pathname.toLowerCase();              // Normalize case
        u.pathname = u.pathname.replace(/\/+/g, '/');       // Remove duplicate slashes
        u.pathname = ensureTrailingSlash(u.pathname);       // Ensure trailing slash

        return u.origin + u.pathname;
    } catch {
        // Fallback for malformed or relative inputs
        const fallback = url.toLowerCase().trim();
        return ensureTrailingSlash(fallback);
    }
}

/**
 * Extracts only the pathname from a URL string or raw path and ensures:
 * - Leading slash
 * - trailing slash
 */
export function extractPathnameFromUrlOrPath(urlOrPath: string): string {
    try {
        const u = new URL(urlOrPath);
        return normalizePathOnly(u.pathname);
    } catch {
        return normalizePathOnly(urlOrPath);
    }
}

function normalizePathOnly(path: string): string {
    if (!path.startsWith('/')) {
        path = '/' + path;
    }
    path = path.replace(/\/+$/, '') || '/';
    return ensureTrailingSlash(path);
}

/**
 * Ensures a trailing slash on a given path, unless it's the root.
 * Example: '/items/abc' → '/items/abc/'
 */
export function ensureTrailingSlash(path: string): string {
    return path === '/' ? '/' : path.replace(/\/?$/, '/');
}

/**
 * Formats a given pathname string by performing the following:
 * - Removes leading and trailing slashes
 * - Gets the last path segment
 * - Removes file extension
 * - Replaces hyphens/underscores with spaces
 * - Capitalizes each word
 */
export function formatPathSegmentToTitle(pathname: string): string {
    const trimmed = pathname.replace(/^\/+|\/+$/g, '');
    const segments = trimmed.split('/');
    let lastSegment = segments[segments.length - 1];
    lastSegment = lastSegment.replace(/\.[^.]+$/, '');         // Remove file extension
    lastSegment = lastSegment.replace(/[-_]/g, ' ');           // Hyphens/underscores to spaces
    return lastSegment.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()); // Title Case
}